// src/components/admin/decisions/RejectPaper.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { 
  Send,
  Mail,
  User,
  FileText,
  X,
  Paperclip,
  AlertCircle,
  CheckCircle,
  Copy,
  Link as LinkIcon,
  ThumbsDown,
  RefreshCw,
  Home
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from '../../utils/axios';

const RejectPaper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const quillRef = useRef(null);
  const quillInstance = useRef(null);
  const { user } = useAuth();
  
  // Parse URL parameters
  const queryParams = new URLSearchParams(location.search);
  const manuscriptId = queryParams.get('a');

  // State for manuscript data
  const [manuscriptData, setManuscriptData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // State for form fields
  const [formData, setFormData] = useState({
    articleId: manuscriptId,
    editor: user?.email || '',
    editorName: user?.fullname || 'ASFI Editorial Office',
    editorDesignation: user?.designation || 'Associate Editor',
    reviewerEmail: '',
    ccEmail: '',
    bccEmail: '',
    subject: '',
  });

  // State for email tags
  const [ccTags, setCcTags] = useState([]);
  const [bccTags, setBccTags] = useState([]);
  const [ccInput, setCcInput] = useState('');
  const [bccInput, setBccInput] = useState('');

  // State for attachments
  const [attachments, setAttachments] = useState([]);
  const [attachmentPreview, setAttachmentPreview] = useState([]);
  
  // State for UI
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [copySuccess, setCopySuccess] = useState('');

  // Fetch manuscript data
  useEffect(() => {
    const fetchManuscriptData = async () => {
      if (!manuscriptId) {
        setFetchError('No manuscript ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.post('/editors/getSubmissionData', { id: manuscriptId });
        
        if (response.data.error) {
          setFetchError(response.data.error);
          setManuscriptData(null);
        } else if (response.data.success && response.data.articles) {
          const article = response.data.articles;
          
          // Parse authors if it's a JSON string
          let authors = [];
          try {
            authors = typeof article.authors === 'string' 
              ? JSON.parse(article.authors) 
              : article.authors || [];
          } catch (e) {
            authors = [article.corresponding_author_fullname];
          }

          const data = {
            id: article.revision_id,
            title: article.title || 'Untitled',
            correspondingAuthor: article.corresponding_author_fullname || article.corresponding_author || '',
            correspondingEmail: article.corresponding_authors_email || article.corresponding_email || '',
            submittedDate: article.created_at || '',
            type: article.article_type || 'Research Article',
            authors: authors
          };
          
          setManuscriptData(data);
          
          // Update form data with fetched information
          setFormData(prev => ({
            ...prev,
            reviewerEmail: data.correspondingEmail,
            subject: `Decision on Your Manuscript: ${data.title} (${manuscriptId})`,
          }));
          
          setFetchError(null);
        }
      } catch (error) {
        console.error('Error fetching manuscript:', error);
        setFetchError(error.response?.data?.error || 'Failed to fetch manuscript data');
        setManuscriptData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchManuscriptData();
  }, [manuscriptId]);

  // Initialize Quill editor
  useEffect(() => {
    if (!quillRef.current || !manuscriptData) return;

    // Check if Quill is already initialized
    if (quillInstance.current) {
      return;
    }

    quillInstance.current = new Quill(quillRef.current, {
      modules: {
        toolbar: [
          ['bold', 'italic'],
          ['link', 'blockquote', 'code-block'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ 'header': [1, 2, false] }],
          [{ 'color': [] }],
          ['clean']
        ],
      },
      theme: 'snow',
      placeholder: 'Compose your rejection letter...',
    });

    // Load initial content
    updateTemplate();

    return () => {
      // Cleanup if needed
    };
  }, [manuscriptData]);

  // Update template when manuscriptData changes
  useEffect(() => {
    if (quillInstance.current && manuscriptData) {
      updateTemplate();
    }
  }, [manuscriptData, formData.editorName, formData.editorDesignation]);

  const updateTemplate = () => {
    if (!quillInstance.current || !manuscriptData) return;
    
    const template = getFilledTemplate();
    quillInstance.current.setContents(template);
  };

  const getFilledTemplate = () => {
    const today = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    return [
      { insert: `${today}\n\n` },
      { insert: 'Dear ' },
      { attributes: { color: '#e60000', bold: true }, insert: manuscriptData.correspondingAuthor },
      { insert: `,\n\nThank you for submitting your manuscript "${manuscriptData.title}" (${manuscriptId}) to ASFI Research Journal (ASFIRJ), which has been evaluated by external reviewers and the editor.\n\nWe regret to inform you that your manuscript has not been accepted for publication in the ASFIRJ.\n\nIn addition to the criticism raised by the reviewers on your manuscript, the editorial judgment is that your manuscript did not reach a sufficient priority score that would warrant publication in ASFIRJ. It was, therefore, rejected after external revision.\n\nWhen evaluating whether a manuscript can be accepted for publication in ASFIRJ, the editors must consider several aspects of a submitted work, including the scientific value of the manuscript and the interest to the readers. Other aspects considered in reaching a decision on a manuscript are:\n\n•\tThe strength of the data used\n•\tNovelty of the concept\n•\tMethodological and experimental approach used\n•\tResults and appropriateness of presentation\n•\tScope of the manuscript and general interest of our readers\n•\tLevel of the presented evidence for the main findings and soundness of their discussion\n\nWe regret having to send you a negative decision on this occasion. We appreciate the time and effort you have expended in carrying out your study and giving us the opportunity to review it. We welcome the opportunity to review material from you and your colleagues in the future.\n\nSincerely,\n\n` },
      { attributes: { color: '#e60000', bold: true }, insert: formData.editorName },
      { insert: '\n' },
      { attributes: { color: '#e60000', bold: true }, insert: formData.editorDesignation },
      { insert: '\n\n' },
      { attributes: { link: 'mailto:submissions@asfirj.org' }, insert: 'submissions@asfirj.org' },
      { insert: '\n\nASFI Research Journal\nExcellence. Quality. Impact\n"Raising the bar of scientific publishing in Africa"\n' },
      { attributes: { link: 'https://asfirj.org/' }, insert: 'https://asfirj.org/' },
      { insert: '\n' },
      { attributes: { link: 'mailto:submissions@asfirj.org' }, insert: 'submissions@asfirj.org' },
      { insert: '\nLinkedIn: ' },
      { attributes: { link: 'www.linkedin.com/in/asfi-research-journal-1b9929309' }, insert: 'www.linkedin.com/in/asfi-research-journal-1b9929309' },
      { insert: '\nX (formerly Twitter): ' },
      { attributes: { link: 'https://twitter.com/asfirj1' }, insert: 'https://twitter.com/asfirj1' },
      { insert: '\nInstagram: ' },
      { attributes: { link: ' https://www.instagram.com/asfirj1/' }, insert: ' https://www.instagram.com/asfirj1/' },
      { insert: '\nWhatsApp: ' },
      { attributes: { link: 'https://chat.whatsapp.com/L8o0N0pUieOGIUHJ1hjSG3' }, insert: 'https://chat.whatsapp.com/L8o0N0pUieOGIUHJ1hjSG3' },
      { insert: '\n\n\nThe full comments of the reviewers are reproduced below:\n' },
      { attributes: { color: '#e60000', bold: true }, insert: '[Enter Reviewer\'s Comments]' },
      { insert: '\n' }
    ];
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle CC email input
  const handleCcKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addCcEmail();
    } else if (e.key === 'Backspace' && ccInput === '' && ccTags.length > 0) {
      removeCcTag(ccTags.length - 1);
    }
  };

  const handleCcBlur = () => {
    if (ccInput.trim()) {
      addCcEmail();
    }
  };

  const addCcEmail = () => {
    const email = ccInput.trim().replace(/,/g, '');
    if (email && isValidEmail(email) && !ccTags.includes(email)) {
      setCcTags([...ccTags, email]);
      setFormData(prev => ({ 
        ...prev, 
        ccEmail: [...ccTags, email].join(', ')
      }));
      setCcInput('');
    }
  };

  // Handle BCC email input
  const handleBccKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addBccEmail();
    } else if (e.key === 'Backspace' && bccInput === '' && bccTags.length > 0) {
      removeBccTag(bccTags.length - 1);
    }
  };

  const handleBccBlur = () => {
    if (bccInput.trim()) {
      addBccEmail();
    }
  };

  const addBccEmail = () => {
    const email = bccInput.trim().replace(/,/g, '');
    if (email && isValidEmail(email) && !bccTags.includes(email)) {
      setBccTags([...bccTags, email]);
      setFormData(prev => ({ 
        ...prev, 
        bccEmail: [...bccTags, email].join(', ')
      }));
      setBccInput('');
    }
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const removeCcTag = (indexToRemove) => {
    const newTags = ccTags.filter((_, index) => index !== indexToRemove);
    setCcTags(newTags);
    setFormData(prev => ({ 
      ...prev, 
      ccEmail: newTags.join(', ')
    }));
  };

  const removeBccTag = (indexToRemove) => {
    const newTags = bccTags.filter((_, index) => index !== indexToRemove);
    setBccTags(newTags);
    setFormData(prev => ({ 
      ...prev, 
      bccEmail: newTags.join(', ')
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const totalFiles = attachments.length + files.length;

    if (totalFiles > 10) {
      setMessage({
        type: 'error',
        text: 'Maximum 10 files allowed'
      });
      return;
    }

    setAttachments(prev => [...prev, ...files]);

    // Create preview URLs
    const newPreviews = files.map(file => ({
      name: file.name,
      size: (file.size / 1024).toFixed(2),
      url: URL.createObjectURL(file)
    }));
    setAttachmentPreview(prev => [...prev, ...newPreviews]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    setAttachmentPreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(label);
    setTimeout(() => setCopySuccess(''), 2000);
  };

  const handleResetTemplate = () => {
    if (quillInstance.current && manuscriptData) {
      updateTemplate();
      setMessage({
        type: 'success',
        text: 'Template reset with latest data'
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowConfirmModal(true);
  };

  const confirmSend = async () => {
    setSending(true);
    setShowConfirmModal(false);

    try {
      // Get editor content - send as the raw Delta ops array
      const editorContent = quillInstance.current.getContents();
      
      // Prepare form data
      const formDataToSend = new FormData();
      formDataToSend.append('articleId', formData.articleId);
      formDataToSend.append('reviewerEmail', formData.reviewerEmail);
      formDataToSend.append('ccEmail', formData.ccEmail);
      formDataToSend.append('bccEmail', formData.bccEmail);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('editorName', formData.editorName);
      formDataToSend.append('editorDesignation', formData.editorDesignation);
      
      // Send the entire Delta object
      formDataToSend.append('message', JSON.stringify(editorContent.ops || editorContent));
      formDataToSend.append('decision', 'reject');
      
      attachments.forEach(file => {
        formDataToSend.append('attachments[]', file);
      });

      // Send to the endpoint (you'll need to create this endpoint)
      const response = await axios.post('/editors/email/rejectPaper', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 60 second timeout for large files
      });

      if (response.data.status === 'success') {
        setMessage({
          type: 'success',
          text: response.data.message || 'Rejection letter sent successfully'
        });

        setTimeout(() => {
          setMessage({ type: '', text: '' });
          navigate('/editors/dashboard');
        }, 3000);
      } else if (response.data.status === 'warning') {
        setMessage({
          type: 'warning',
          text: response.data.message || 'Email already sent'
        });
        setSending(false);
      } else {
        setMessage({
          type: 'error',
          text: response.data.message || 'Failed to send email'
        });
        setSending(false);
      }

    } catch (error) {
      console.error('Error sending email:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || error.message || 'Failed to send rejection letter. Please try again.'
      });
      setSending(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="container-fluid px-4 py-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading manuscript data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error/Not Found state
  if (fetchError || !manuscriptData) {
    return (
      <div className="container-fluid px-4 py-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Manuscript Not Found</h3>
            <p className="text-gray-600 mb-6">
              {fetchError || 'No manuscript data found for the provided ID'}
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500">
                <strong>Manuscript ID:</strong> {manuscriptId || 'Not provided'}
              </p>
            </div>
            <button
              onClick={() => navigate('/editors/dashboard')}
              className="inline-flex items-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Home className="w-5 h-5 mr-2" />
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4 py-6">
      {/* Message Display */}
      {message.text && (
        <div className={`mb-4 p-4 rounded-lg flex items-center ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-700' 
            : message.type === 'warning'
            ? 'bg-yellow-50 border border-yellow-200 text-yellow-700'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 mr-2" />
          ) : (
            <AlertCircle className="w-5 h-5 mr-2" />
          )}
          {message.text}
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h3 className="text-2xl font-bold text-gray-800 flex items-center">
            <ThumbsDown className="w-6 h-6 mr-2 text-red-600" />
            Reject Manuscript
          </h3>
          <button
            type="button"
            onClick={handleResetTemplate}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
            title="Reset template with latest data"
          >
            <RefreshCw className="w-4 h-4" />
            Reset Template
          </button>
        </div>
        <p className="text-gray-600 mt-1">
          Send rejection letter for manuscript {manuscriptId}
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit} id="sendMail">
          <input type="hidden" name="articleId" value={manuscriptId} readOnly />

          {/* Recipient Section */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Recipient</h4>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Corresponding Author Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  name="reviewerEmail"
                  id="email"
                  value={formData.reviewerEmail}
                  onChange={handleInputChange}
                  readOnly
                  required
                />
              </div>

              {/* CC Field with Tags */}
              <div>
                <label htmlFor="ccEmail" className="block text-sm font-medium text-gray-700 mb-1">
                  CC
                </label>
                <div className="flex flex-wrap items-center gap-2 p-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-red-500 focus-within:border-transparent min-h-[42px]">
                  {ccTags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-md text-sm bg-blue-100 text-blue-700"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeCcTag(index)}
                        className="ml-1 hover:text-blue-900"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="flex-1 min-w-[200px] outline-none border-0 p-1 text-sm"
                    value={ccInput}
                    onChange={(e) => setCcInput(e.target.value)}
                    onKeyDown={handleCcKeyDown}
                    onBlur={handleCcBlur}
                    placeholder={ccTags.length === 0 ? "Enter CC emails (comma separated)" : ""}
                  />
                </div>
              </div>

              {/* BCC Field with Tags */}
              <div>
                <label htmlFor="bccEmail" className="block text-sm font-medium text-gray-700 mb-1">
                  BCC
                </label>
                <div className="flex flex-wrap items-center gap-2 p-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-red-500 focus-within:border-transparent min-h-[42px]">
                  {bccTags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-md text-sm bg-gray-100 text-gray-700"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeBccTag(index)}
                        className="ml-1 hover:text-gray-900"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="flex-1 min-w-[200px] outline-none border-0 p-1 text-sm"
                    value={bccInput}
                    onChange={(e) => setBccInput(e.target.value)}
                    onKeyDown={handleBccKeyDown}
                    onBlur={handleBccBlur}
                    placeholder={bccTags.length === 0 ? "Enter BCC emails (comma separated)" : ""}
                  />
                </div>
              </div>

              <p className="text-xs text-gray-500">* Press Enter, Space, or Comma to add email addresses</p>
            </div>

            {/* Copyable Information */}
            {manuscriptData && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h5 className="text-sm font-medium text-blue-800 mb-3 flex items-center">
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Click to Copy
                </h5>
                <ul className="space-y-2">
                  <li>
                    <button
                      type="button"
                      onClick={() => handleCopy(`${manuscriptData.title} (${manuscriptId})`, 'title')}
                      className="text-sm text-blue-700 hover:text-blue-900 hover:underline flex items-center"
                    >
                      <FileText className="w-3 h-3 mr-2" />
                      Manuscript Title And ID
                      {copySuccess === 'title' && (
                        <span className="ml-2 text-xs text-green-600">Copied!</span>
                      )}
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => handleCopy(manuscriptData.correspondingAuthor, 'author')}
                      className="text-sm text-blue-700 hover:text-blue-900 hover:underline flex items-center"
                    >
                      <User className="w-3 h-3 mr-2" />
                      Corresponding Author's Name
                      {copySuccess === 'author' && (
                        <span className="ml-2 text-xs text-green-600">Copied!</span>
                      )}
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Subject Section */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Subject</h4>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              name="subject"
              id="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Message Body Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-800">Message Body</h4>
              <div className="text-sm text-gray-600 bg-blue-50 px-3 py-1 rounded-full">
                Auto-filled with manuscript data
              </div>
            </div>
            
            {/* Placeholder Info */}
            {manuscriptData && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                <div className="text-sm">
                  <span className="font-medium text-gray-700">[Manuscript Title]:</span>
                  <span className="ml-2 text-gray-600 truncate block">{manuscriptData.title.substring(0, 30)}...</span>
                </div>
                <div className="text-sm">
                  <span className="font-medium text-gray-700">[Manuscript ID]:</span>
                  <span className="ml-2 text-gray-600">{manuscriptId}</span>
                </div>
                <div className="text-sm">
                  <span className="font-medium text-gray-700">[Corresponding Author]:</span>
                  <span className="ml-2 text-gray-600">{manuscriptData.correspondingAuthor}</span>
                </div>
                <div className="text-sm">
                  <span className="font-medium text-gray-700">[Editor Name]:</span>
                  <span className="ml-2 text-gray-600">{formData.editorName}</span>
                </div>
              </div>
            )}

            {/* Quill Editor */}
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <div ref={quillRef} style={{ height: '400px' }} />
            </div>
            
            <p className="text-xs text-gray-500 mt-2">
              * The message has been automatically populated with manuscript data. Please replace the reviewer comments placeholder with the actual reviewer feedback.
            </p>
          </div>

          {/* Attachments Section */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">
              Attachments (Max: 10 Files)
            </h4>
            
            <input
              type="file"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              name="attachments[]"
              id="attachmentInput"
              multiple
              onChange={handleFileChange}
              accept="*/*"
            />

            {/* Attachment Preview */}
            {attachmentPreview.length > 0 && (
              <div className="mt-4 space-y-2" id="attachmentPreviewContainer">
                {attachmentPreview.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <Paperclip className="w-4 h-4 text-gray-400 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">{file.name}</p>
                        <p className="text-xs text-gray-500">{file.size} KB</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="p-1 text-gray-400 hover:text-red-500 hover:bg-white rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-center mt-8">
            <button
              type="submit"
              disabled={sending}
              className="px-8 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Rejection Letter
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <ThumbsDown className="w-6 h-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Confirm Rejection</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Are you sure you want to send this rejection letter to {formData.reviewerEmail}?
                  </p>
                </div>
              </div>

              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> This action will formally reject the manuscript. This decision is final.
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSend}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <ThumbsDown className="w-4 h-4" />
                  Confirm Rejection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="footer text-center py-4 border-t border-gray-200 mt-8">
        <p className="text-sm text-gray-500">
          {new Date().getFullYear()} © ASFI - all rights reserved
        </p>
      </footer>
    </div>
  );
};

export default RejectPaper;